if (!customElements.get('media-gallery')) {
  customElements.define('media-gallery', class MediaGallery extends HTMLElement {
    constructor() {
      super();
      this.elements = {
        liveRegion: this.querySelector('[id^="GalleryStatus"]'),
        viewer: this.querySelector('[id^="GalleryViewer"]'),
        thumbnails: this.querySelector('[id^="GalleryThumbnails"]')
      };
      this.mql = window.matchMedia('(min-width: 750px)');

      this.sectionId = (this.id || '')
        .replace(/^MediaGallery-/, '')
        .replace(/-duplicate$/, '');
      this.variantChangeUnsubscriber = subscribe(
        PUB_SUB_EVENTS.variantChange,
        this._vfOnVariantChange.bind(this)
      );

      if (!this.elements.thumbnails) return;

      this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
      this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
        mediaToSwitch.querySelector('button').addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
      });
      if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
    }

    disconnectedCallback() {
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
        this.variantChangeUnsubscriber = null;
      }
    }

    connectedCallback() {
      if (!this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber = subscribe(
          PUB_SUB_EVENTS.variantChange,
          this._vfOnVariantChange.bind(this)
        );
      }
    }

    onSlideChanged(event) {
      if (!event || !event.detail || !event.detail.currentElement) return;
      const thumbnail = this.elements.thumbnails.querySelector(
        `[data-target="${event.detail.currentElement.dataset.mediaId}"]`
      );
      if (thumbnail) {
        this.setActiveThumbnail(thumbnail);
      }
    }

    setActiveMedia(mediaId, prepend) {
      const activeMedia = this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`);
      if (!activeMedia) return;

      this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
        element.classList.remove('is-active');
      });
      activeMedia.classList.add('is-active');

      if (prepend) {
        activeMedia.parentElement.prepend(activeMedia);
        if (this.elements.thumbnails) {
          const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
          if (activeThumbnail) activeThumbnail.parentElement.prepend(activeThumbnail);
        }
        if (this.elements.viewer.slider) this.elements.viewer.resetPages();
      }

      this.preventStickyHeader();
      window.setTimeout(() => {
        if (this.elements.thumbnails) {
          activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
        }
        if (!this.elements.thumbnails || this.dataset.desktopLayout === 'stacked') {
          activeMedia.scrollIntoView({ behavior: 'smooth' });
        }
      });
      this.playActiveMedia(activeMedia);

      if (!this.elements.thumbnails) return;
      const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
      this.setActiveThumbnail(activeThumbnail);
      if (activeThumbnail) {
        this.announceLiveRegion(activeMedia, activeThumbnail.dataset.mediaPosition);
      }
    }

    setActiveThumbnail(thumbnail) {
      if (!this.elements.thumbnails || !thumbnail) return;

      this.elements.thumbnails.querySelectorAll('button').forEach((element) => element.removeAttribute('aria-current'));
      thumbnail.querySelector('button').setAttribute('aria-current', true);

      this.elements.thumbnails.querySelectorAll('.thumbnail').forEach((element) => {
        element.classList.remove('is-active');
      });

      thumbnail.querySelector('.thumbnail').classList.add('is-active');

      if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

      this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
    }

    announceLiveRegion(activeItem, position) {
      const image = activeItem.querySelector('.product__modal-opener--image img');
      if (!image) return;
      image.onload = () => {
        this.elements.liveRegion.setAttribute('aria-hidden', false);
        this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace(
          '[index]',
          position
        );
        setTimeout(() => {
          this.elements.liveRegion.setAttribute('aria-hidden', true);
        }, 2000);
      };
      image.src = image.src;
    }

    playActiveMedia(activeItem) {
      window.pauseAllMedia();
      const deferredMedia = activeItem.querySelector('.deferred-media');
      if (deferredMedia) deferredMedia.loadContent(false);
    }

    preventStickyHeader() {
      this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
      if (!this.stickyHeader) return;
      this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
    }

    removeListSemantic() {
      if (!this.elements.viewer.slider) return;
      this.elements.viewer.slider.setAttribute('role', 'presentation');
      this.elements.viewer.sliderItems.forEach(slide => slide.setAttribute('role', 'presentation'));
    }

    static _vfSlugify(str) {
      return String(str)
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, '-')
        .replace(/^-+|-+$/g, '');
    }

    static _vfVariantSlug(variant) {
      return variant.options.map(MediaGallery._vfSlugify).join('-');
    }

    static _vfTokenList(slugAttr) {
      const s = (slugAttr || '').trim();
      return s === '' ? [] : s.split(' ').filter(Boolean);
    }

    static _vfShouldShow(slugAttr, variantSlug) {
      const tokens = MediaGallery._vfTokenList(slugAttr);
      if (tokens.length === 0) return true;
      return tokens.includes(variantSlug);
    }

    _vfApplyFilter(variantSlug) {
      if (!this.elements.viewer) return;

      const allViewerItems = Array.from(
        this.elements.viewer.querySelectorAll('.product-media-item[data-media-id]')
      );

      const hasAnyTag = allViewerItems.some(
        (el) => MediaGallery._vfTokenList(el.dataset.vfSlug).length > 0
      );
      if (!hasAnyTag) return;

      let visibleCount = 0;
      allViewerItems.forEach((item) => {
        const show = MediaGallery._vfShouldShow(item.dataset.vfSlug, variantSlug);
        item.classList.toggle('product-media-item--vf-hidden', !show);
        if (show) visibleCount++;
      });

      if (visibleCount === 0) {
        allViewerItems.forEach((item) => {
          const isShared = MediaGallery._vfTokenList(item.dataset.vfSlug).length === 0;
          item.classList.toggle('product-media-item--vf-hidden', !isShared);
          if (isShared) visibleCount++;
        });
      }

      if (visibleCount === 0) {
        allViewerItems.forEach((item) => item.classList.remove('product-media-item--vf-hidden'));
      }

      if (this.elements.thumbnails) {
        this.elements.thumbnails.querySelectorAll('.thumbnail-list__item[data-target]').forEach((item) => {
          const show = MediaGallery._vfShouldShow(item.dataset.vfSlug, variantSlug);
          item.classList.toggle('thumbnail-list__item--vf-hidden', !show);
        });
        if (typeof this.elements.thumbnails.init === 'function') {
          this.elements.thumbnails.init();
        }
      }

      const modal = document.querySelector(`#ProductModal-${this.sectionId}`);
      if (modal) {
        modal.querySelectorAll('[data-vf-media-id]').forEach((item) => {
          const show = MediaGallery._vfShouldShow(item.dataset.vfSlug, variantSlug);
          item.classList.toggle('product-media-modal__media--vf-hidden', !show);
        });
      }

      // setTimeout(0) allows the current synchronous variant-change processing
      // to settle before we inspect and correct the active media state.
      // It does not guarantee ordering with respect to microtasks or other
      // asynchronous callbacks.
      window.setTimeout(() => {
        if (!this.elements.viewer) return;
        const currentActive = this.elements.viewer.querySelector('.product-media-item.is-active');
        const activeIsHidden = currentActive &&
          currentActive.classList.contains('product-media-item--vf-hidden');

        if (!currentActive || activeIsHidden) {
          const firstVisible = this.elements.viewer.querySelector(
            '.product-media-item[data-media-id]:not(.product-media-item--vf-hidden)'
          );
          if (firstVisible && firstVisible.dataset.mediaId) {
            this.setActiveMedia(firstVisible.dataset.mediaId, false);
          }
        }
      }, 0);
    }

    _vfOnVariantChange(event) {
      if (!event || !event.data) return;
      if (event.data.sectionId !== this.sectionId) return;

      const variant = event.data.variant;
      if (!variant || !Array.isArray(variant.options)) return;

      this._vfApplyFilter(MediaGallery._vfVariantSlug(variant));
    }
  });
}

class VerticalThumbnailSlider extends HTMLElement {
  constructor() {
    super();

    this.slider = this.querySelector("ul.slider");
    this.sliderItems = this.querySelectorAll("li.slider-slide");
    this.prevButton = this.querySelector('button[name="previous"]');
    this.nextButton = this.querySelector('button[name="next"]');
    this.step = parseInt(this.prevButton?.dataset.step || "1", 10);
    this.sliderItemHeight = 0;

    if (!this.slider || !this.nextButton) return;

    this.init();

    this.prevButton.addEventListener("click", this.scrollUp.bind(this));
    this.nextButton.addEventListener("click", this.scrollDown.bind(this));
    this.slider.addEventListener("scroll", this.updateButtons.bind(this));

    const resizeObserver = new ResizeObserver(() => this.init());
    resizeObserver.observe(this.slider);
  }

  init() {
    this.sliderItems = this.querySelectorAll("li.slider-slide");
    if (this.sliderItems.length < 2) return;

    this.sliderItemHeight =
      this.sliderItems[1].offsetTop - this.sliderItems[0].offsetTop;

    this.updateButtons();
  }

  scrollUp(e) {
    e.preventDefault();
    this.slider.scrollBy({ top: -this.step * this.sliderItemHeight, behavior: "smooth" });
  }

  scrollDown(e) {
    e.preventDefault();
    this.slider.scrollBy({ top: this.step * this.sliderItemHeight, behavior: "smooth" });
  }

  updateButtons() {
    if (!this.slider) return;
    const scrollTop = this.slider.scrollTop;
    const maxScrollTop = this.slider.scrollHeight - this.slider.clientHeight;
    this.prevButton.style.display = scrollTop <= 0 ? "none" : "flex";
    this.nextButton.style.display = scrollTop >= maxScrollTop - 1 ? "none" : "flex";
  }

  isSlideVisible(element, offset = 0) {
    const lastVisibleSlide = this.slider.scrollTop + this.slider.clientHeight - offset;
    return (
      element.offsetTop + element.clientHeight <= lastVisibleSlide &&
      element.offsetTop >= this.slider.scrollTop
    );
  }
}

customElements.define("vertical-thumbnail-slider", VerticalThumbnailSlider);
